import React from "react";
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    InputAdornment,
    ListItem,
    ListItemText,
    OutlinedInput,
    Popover,
    Tooltip,
    Typography
} from "@mui/material";
import { ChevronDownIcon, CloseIcon } from "../../components/icons/MANIcons";
import useTranslation from "../../hooks/usetranslation";
import {Virtuoso, VirtuosoHandle} from "react-virtuoso";
import IconButton from "@mui/material/IconButton";

export type Option = { value: string; label: string };

type CustomMultiSelectProps = {
    label?: string;
    options: Option[];
    value: string[];
    error?: boolean;
    helperText?: string;
    onChange: (newValues: string[]) => void;
    disabled?: boolean;
    searchable?: boolean;
};

const FormSelectCodeCatalog: React.FC<CustomMultiSelectProps> = ({
                                                                     label,
                                                                     options,
                                                                     value,
                                                                     error,
                                                                     onChange,
                                                                     disabled = false,
                                                                     searchable = false
                                                                 }) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const virtuosoRef = React.useRef<VirtuosoHandle>(null);
    const [localValue, setLocalValue] = React.useState<string[]>(value);
    const [searchTerm, setSearchTerm] = React.useState("");
    const [optionsFiltered, setOptionsFiltered] = React.useState<Option[]>(options);
    const { t } = useTranslation();

    const open = Boolean(anchorEl);

    React.useEffect(() => {
        if (open) {
            setLocalValue(value);
        }
    }, [open, value]);

    React.useEffect(() => {
        if (searchable && searchTerm) {
            setOptionsFiltered(
                options.filter(opt =>
                    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
                )
            );
        } else {
            setOptionsFiltered(options);
        }
    }, [searchTerm, options, searchable]);

    const handleToggle = (opt: string) => () => {
        setLocalValue((prev) =>
            prev.includes(opt)
                ? prev.filter((v) => v !== opt)
                : [...prev, opt]
        );
    };

    const handleFieldClick = (e: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(e.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleOk = () => {
        onChange(localValue);
        handleClose();
    };

    const handleCancel = () => {
        handleClose();
    };

    const displayValue = options
        .filter((o) => value.includes(o.value))
        .map((o) => o.label)
        .join(", ");

    return (
        <>
            <FormControl error={error} sx={{ width: "100%" }}>
                {label && <Typography variant="subtitle2">{label}</Typography>}
                <OutlinedInput
                    value={displayValue}
                    onClick={disabled ? undefined : handleFieldClick}
                    readOnly
                    placeholder={t("code_catalogue_select_placeholder")}
                    sx={{
                        width: '100%',
                        height: 45,
                        backgroundColor: disabled ? '#f5f5f5' : 'white',
                        color: disabled ? 'rgba(0,0,0,0.38)' : 'inherit',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#ccc',
                            borderWidth: 1,
                        },
                        '& .MuiOutlinedInput-input': {
                            padding: '4px 0 4px 8px',
                        },
                        '& .MuiInputAdornment-root': {
                            marginRight: 0,
                        },
                    }}
                    endAdornment={
                        <InputAdornment position="end">
                            <ChevronDownIcon sx={{ fontSize: 16, padding: "1px" }} />
                        </InputAdornment>
                    }
                />
            </FormControl>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transitionDuration={0}
                slotProps={{
                    paper: { sx: { width: anchorEl?.clientWidth } }
                }}
            >
                {searchable && (
                    <Box sx={{ padding: 1 }}>
                        <OutlinedInput
                            placeholder={t("search")}
                            fullWidth
                            sx={{ height: 36 }}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            endAdornment={
                                searchTerm && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => setSearchTerm("")}
                                            edge="end"
                                            aria-label="clear search"
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }
                        />
                    </Box>
                )}

                <Box sx={{ width: '100%', height:450, maxHeight:500 }}>
                    <Virtuoso
                        ref={virtuosoRef}
                        totalCount={optionsFiltered.length}
                        itemContent={(index) => {
                            const option = optionsFiltered[index];
                            return (
                                <ListItem
                                    key={option.value}
                                    onClick={handleToggle(option.value)}
                                    component="div"
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'action.hover',
                                            cursor: 'pointer',
                                        },
                                    }}
                                >
                                    <Checkbox
                                        edge="start"
                                        checked={localValue.includes(option.value)}
                                        tabIndex={-1}
                                        disableRipple
                                    />
                                    <Tooltip title={option.label} placement="top" arrow>
                                        <ListItemText sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            flexGrow: 1,
                                            minWidth: 0,
                                        }} primary={option.label} />
                                    </Tooltip>
                                </ListItem>
                            );
                        }}
                    />
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1,
                        mt: 1,
                        padding: 1,
                    }}
                >
                    <Button size="small" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button size="small" variant="contained" onClick={handleOk}>
                        Ok
                    </Button>
                </Box>
            </Popover>
        </>
    );
};

export default FormSelectCodeCatalog;
